import { useState } from 'react';
import { Grid, Button, TextField } from '@material-ui/core';
import { DialogActions, DialogContentText, DialogContent, Dialog, DialogTitle } from '@material-ui/core';
import { MenuItem } from '@material-ui/core/';
import { Alert, AlertTitle } from '@material-ui/lab';
import SuccessDialog from '../Dialogs/SuccessDialog';
import { Company } from '../../models/location.interface';
import { Locations } from '../../api/api';
import { isEmpty } from "lodash";
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    gridSpacer: {
      minHeight: '12px',
    },
  }),
);

export interface IAddLocationDialog {
  open: boolean
  onClose: () => void
  loadAllLocations: () => void
  companyList: Company[]
}

export const AddLocationDialog = ({
  open, onClose, loadAllLocations, companyList
}: IAddLocationDialog) => {
  const classes = useStyles(); 
  const [ saved, setSaved ] = useState<boolean>(false);
  const [ serviceError, setServiceError ] = useState<string>('');
  const [ name, setName ] = useState<string>('');
  const [ phoneNumber, setPhoneNumber ] = useState<string>('');
  const [ companyId, setCompanyId ] = useState<string>('');
  const [ nameError, setNameError ] = useState<string>('');
  const [ phoneNumberError, setPhoneNumberError ] = useState<string>('');
  const [ companyError, setCompanyError ] = useState<string>('')

  const resetFields = () => {
    setServiceError('')
    setName('')
    setPhoneNumber('')
    setCompanyId('')
    setNameError('')
    setPhoneNumberError('')
    setCompanyError('')
  }

  const onCloseHandler = () => {
    resetFields();
    setSaved(false);
    onClose();
  }

  const isFormValid = () => {
    validateField('name', name)
    validateField('company', companyId)
    validateField('phoneNumber', phoneNumber)
    
    return isEmpty(companyError) && isEmpty(nameError) && isEmpty(phoneNumberError) 
  }

  const validateField = (id: string, value: string) => {
    if(id === 'name'){
      isEmpty(value) ? setNameError('Required') : setNameError('')
    } else if(id === 'company'){
      isEmpty(value) || value === '-1' ? setCompanyError('Required') : setCompanyError('')
    } else if(id === 'phoneNumber'){
      isEmpty(value) ? setPhoneNumberError('Required') : setPhoneNumberError('')
    }
  }

  const handleSubmitClick = () => {
    if(isFormValid()){
      Locations.createLocation(companyId, name, phoneNumber)
        .then((loc) => {
          setSaved(true);
          resetFields();
          loadAllLocations();
        })
        .catch((e) => {
          setSaved(false);
          setServiceError(e.response.data.message)
        })
    } else {
      setSaved(false);
    }
  }

  return (     
    <div>
      <SuccessDialog 
        open={saved && serviceError.length === 0}
        onClose={onCloseHandler}
        title='Add New Location'
        content='New Location Saved'
      />
      <Dialog 
        onClose={onCloseHandler} 
        aria-labelledby="add-location-title" 
        aria-describedby="add-location-text"
        open={open}
        fullWidth={true}
        maxWidth={'xs'}
      >
        <DialogTitle id="add-location-title">Add New Location</DialogTitle>
        <DialogContent>
          <DialogContentText id="add-location-text">   
            {serviceError.length > 0 
              &&
              <Grid item xs={12}>
                <Alert severity="error" >
                  <AlertTitle>Error</AlertTitle>
                  {serviceError}
                </Alert>   
                <Grid container className={classes.gridSpacer} />
              </Grid>
            } 
            <TextField
              id="name"
              label="Name"
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setName(e.target.value)} 
              onBlur={(e) => validateField(e.target.id, e.target.value)}
              error={nameError.length > 0}
              helperText={nameError}
              required
            />
            <TextField
              id="phoneNumber"
              label="Phone Number"
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setPhoneNumber(e.target.value)} 
              onBlur={(e) => validateField(e.target.id, e.target.value)}
              error={phoneNumberError.length > 0}
              helperText={phoneNumberError}
              required
            />
            <TextField
              id="company"
              select
              label="Company"
              fullWidth
              margin="normal"
              name="company"
              variant="outlined" 
              required
              onChange={(e) => setCompanyId(e.target.value)}
              onBlur={(e) => validateField(e.target.id, e.target.value)}
              error={companyError.length > 0}
              helperText={companyError}
              defaultValue="-1"
              >
                <MenuItem value="-1">
                  <em>Select Company</em>
                </MenuItem>
                {companyList.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
            </TextField>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={onCloseHandler}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="primary"
            size="small" 
            onClick={handleSubmitClick}
          >
            Submit 
          </Button>
        </DialogActions>
      </Dialog>   
    </div>
  );
};

export default AddLocationDialog;